/**
 * 이미지 파일에서 width와 height를 추출하는 유틸리티 함수
 * @param file - 이미지 File 객체
 * @returns Promise<{ width: number; height: number }> - 이미지의 자연스러운 너비와 높이
 */
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    
    img.src = url;
  });
};

export const getImageDimensionsFromUrl = (
  url: string,
): Promise<{ width: number; height: number }> => (
  new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  })
);

export interface PreparedImageData {
  originUrl: string;
  width: number;
  height: number;
}

interface CompressImageOptions {
  maxBytes: number;
  maxWidth?: number;
  maxHeight?: number;
  mimeType?: 'image/jpeg' | 'image/webp';
  minQuality?: number;
}

const blobToDataUrl = (blob: Blob): Promise<string> => (
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read image blob'));
    reader.readAsDataURL(blob);
  })
);

const canvasToBlob = (canvas: HTMLCanvasElement, mimeType: string, quality: number): Promise<Blob> => (
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to compress image'));
        return;
      }
      resolve(blob);
    }, mimeType, quality);
  })
);

const loadImageFromFile = (file: File): Promise<HTMLImageElement> => (
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  })
);

export const compressImageFile = async (
  file: File,
  {
    maxBytes,
    maxWidth,
    maxHeight,
    mimeType = 'image/jpeg',
    minQuality = 0.72,
  }: CompressImageOptions
): Promise<PreparedImageData> => {
  const img = await loadImageFromFile(file);
  const widthScale = maxWidth ? maxWidth / img.naturalWidth : 1;
  const heightScale = maxHeight ? maxHeight / img.naturalHeight : 1;
  let scale = Math.min(1, widthScale, heightScale);
  let width = Math.max(1, Math.round(img.naturalWidth * scale));
  let height = Math.max(1, Math.round(img.naturalHeight * scale));
  let quality = 0.85;

  for (let attempt = 0; attempt < 24; attempt += 1) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas context unavailable');
    }

    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await canvasToBlob(canvas, mimeType, quality);
    if (blob.size <= maxBytes) {
      return {
        originUrl: await blobToDataUrl(blob),
        width,
        height,
      };
    }

    if (quality > minQuality) {
      quality = Math.max(minQuality, quality - 0.05);
    } else {
      scale *= 0.85;
      width = Math.max(1, Math.round(img.naturalWidth * scale));
      height = Math.max(1, Math.round(img.naturalHeight * scale));
    }
  }

  throw new Error('Failed to compress image');
};

/**
 * 파일명에서 확장자를 제거하는 유틸리티 함수
 * @param filename - 파일명 (예: "image.jpg")
 * @returns 확장자가 제거된 파일명 (예: "image")
 */
export const removeFileExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.');
  return lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;
};

export const truncateByCodePoint = (value: string, maxLength: number): string => {
  if (maxLength <= 0) return '';

  const chars = Array.from(value);
  return chars.length > maxLength ? chars.slice(0, maxLength).join('') : value;
};

export const removeFileExtensionWithinLimit = (filename: string, maxLength: number): string => (
  truncateByCodePoint(removeFileExtension(filename), maxLength)
);
